import React from 'react';
import { Link } from 'react-router-dom';
import Icons from '../../Common/Icons/Icons';
import { cn } from '../../../utils/styles';
import SettingsDialog from './SettingsModal';
import HomeLayout from '../../Layout/HomeLayout';

const getApplicationContent = (application: ApplicationType) => (
  <>
    {application === 'eStatements' ? <Icons.GuidanceLogo /> : <Icons.InfoTracLogo width={20} height={20} />}
    {application}
  </>
);

export function ApplicationContainer({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('bg-primary rounded-lg py-1 px-2', className)} {...props}>
      <span className="flex gap-2">{children}</span>
    </div>
  );
}

type ApplicationType = 'eStatements' | 'InfoTRAC' | 'InfoREACH';

interface NavigationItemProps {
  subtitle: string;
  application: ApplicationType;
  icon: React.ReactNode;
  iconType: 'square' | 'circle';
  path: string;
  onBeforeOpen?: () => void;
}

function NavigationItem({ subtitle, icon, iconType, path, application, onBeforeOpen }: NavigationItemProps) {
  const handleMouseDown = () => {
    onBeforeOpen?.();
  };

  return (
    <Link
      to={path}
      onMouseDown={handleMouseDown}
      className={cn([
        'w-56 h-56 p-4 flex flex-col',
        'bg-[#FFFFFF57] rounded-lg flex flex-col justify-center items-center gap-2 text-white',
      ])}
      target="_blank"
      rel="noreferrer"
    >
      <div
        className={cn('nav-icon bg-[#FFFFFF99] rounded-lg text-primary', {
          'rounded-full': iconType === 'circle',
        })}
      >
        <div className="p-5 min-h-[110px] min-w-[110px]">{icon}</div>
      </div>

      <ApplicationContainer className="">{getApplicationContent(application)}</ApplicationContainer>

      <p className="uppercase font-bold">{subtitle}</p>
    </Link>
  );
}

const hardcodedViewer = {
  id: 'viewer-001',
  name: 'John Doe',
  email: 'john_doe@example.com',
  firstName: 'John',
  lastName: 'Doe',
  username: 'john_doe',
  securityQuestion: 'zero',
  securityAnswer: 'New York',
  selectedAccounts: ['****4560', '****4564'],
};

export default function Home(): React.ReactElement {
  const handleAuthorizedUserClick = () => {
    const existing = JSON.parse(localStorage.getItem('tempViewers') || '[]');
    const hasViewer = existing.some((v: any) => v.id === hardcodedViewer.id);

    if (!hasViewer) {
      localStorage.setItem('tempViewers', JSON.stringify([...existing, hardcodedViewer]));
    }
    localStorage.setItem('isAuthorizedUser', 'true');
  };

  const handleRegularLoginClick = () => {
    localStorage.removeItem('isAuthorizedUser');
    localStorage.removeItem('tempViewers');
    localStorage.removeItem('viewerUpdates');
  };

  const items: NavigationItemProps[] = [
    {
      subtitle: '',
      application: 'InfoTRAC',
      icon: <Icons.Chart />,
      iconType: 'circle',
      path: '/infoTrac',
    },
    {
      subtitle: 'Single Sign on',
      application: 'InfoTRAC',
      icon: <Icons.Globe />,
      iconType: 'circle',
      path: '/infoTrac/sign-in',
    },
    {
      subtitle: '',
      application: 'InfoREACH',
      icon: <Icons.PeopleFilled />,
      iconType: 'square',
      path: '/infoReach',
    },
    {
      subtitle: 'Notifications',
      application: 'eStatements',
      icon: <Icons.Letter />,
      iconType: 'circle',
      path: '/guidance/notifications',
    },
    {
      subtitle: 'Single Sign on',
      application: 'eStatements',
      icon: <Icons.GlobeStand />,
      iconType: 'square',
      path: '/guidance/single-sign-on',
      onBeforeOpen: handleRegularLoginClick,
    },
    {
      subtitle: 'Direct Sign on',
      application: 'eStatements',
      icon: <Icons.HouseHome />,
      iconType: 'square',
      path: '/guidance/direct-sign-on',
      onBeforeOpen: handleRegularLoginClick,
    },
  ];

  return (
    <HomeLayout>
      <header
        className="max-w-6xl h-20 mb-4 sm:mb-9 mx-auto px-4 flex gap-2 items-center justify-between text-white
      backdrop-blur supports-[backdrop-filter]:bg-[#2e446bcc]/50
      sm:backdrop-blur-none sm:bg-transparent
      fixed top-0 w-full md:relative md:top-[unset] md:w-[unset]"
      >
        <div className="flex gap-2 items-center text-xl">
          <Icons.InfoImageLogo />
          <div className="flex gap-0.5">
            <span className="italic font-semibold">Info</span>
            <span className="font-extrabold">IMAGE</span>
          </div>
        </div>

        <div>
          <SettingsDialog />
        </div>
      </header>

      <div
        className={cn('menu-items', [
          'max-w-4xl flex gap-4 flex-wrap justify-center items-center m-auto overflow-x-auto',
          'max-h-dvh pt-20 md:max-h-[calc(100vh-120px)] md:pt-0',
        ])}
      >
        {items.map(props => (
          <NavigationItem key={props.path + props.subtitle} {...props} />
        ))}
        <NavigationItem
          subtitle="Authorized Viewer"
          application="eStatements"
          icon={<Icons.GlobeStand />}
          iconType="square"
          path="/guidance/direct-sign-on"
          onBeforeOpen={handleAuthorizedUserClick}
        />
      </div>
    </HomeLayout>
  );
}
